import React, { useState, useEffect, useCallback } from 'react';
import { alertAPI } from '../services/api';
import toast from 'react-hot-toast';

const SEVERITY_BADGE = { info: 'badge-blue', warning: 'badge-amber', critical: 'badge-red' };
const SEVERITY_ICON = { info: 'ℹ️', warning: '⚠️', critical: '🚨' };
const TYPE_ICON = { low_stock: '📦', booking_reminder: '📅', payment_due: '💰', system: '⚙️', custom: '🔔' };

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', message: '', severity: 'info', type: 'custom', targetRole: 'all' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await alertAPI.getAll();
      setAlerts(res.data.data || []);
    } catch { toast.error('Failed to load alerts'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id) => {
    try {
      await alertAPI.markRead(id);
      setAlerts(a => a.map(x => x._id === id ? { ...x, isRead: true } : x));
    } catch {}
  };

  const resolve = async (id) => {
    try {
      await alertAPI.resolve(id);
      toast.success('Alert resolved');
      load();
    } catch { toast.error('Failed to resolve'); }
  };

  const markAllRead = async () => {
    try {
      await alertAPI.markAllRead();
      toast.success('All marked as read');
      load();
    } catch {}
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await alertAPI.create(form);
      toast.success('Alert created');
      setShowModal(false);
      setForm({ title: '', message: '', severity: 'info', type: 'custom', targetRole: 'all' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create alert');
    } finally { setSaving(false); }
  };

  const set = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));
  const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const filtered = alerts.filter(a => {
    const matchFilter = filter === 'all' || (filter === 'unread' && !a.isRead) || (filter === 'resolved' && a.isResolved) || (filter === 'active' && !a.isResolved);
    const matchSeverity = severityFilter === 'all' || a.severity === severityFilter;
    return matchFilter && matchSeverity;
  });

  const unreadCount = alerts.filter(a => !a.isRead && !a.isResolved).length;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>
            Alerts & Notifications
            {unreadCount > 0 && (
              <span style={{ marginLeft: 10, background: 'var(--danger)', color: '#fff', borderRadius: 20, padding: '2px 8px', fontSize: 12, fontWeight: 700 }}>
                {unreadCount}
              </span>
            )}
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 2 }}>{filtered.length} notifications</p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {unreadCount > 0 && (
            <button className="btn btn-ghost" onClick={markAllRead}>Mark all read</button>
          )}
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Alert
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="tabs" style={{ margin: 0 }}>
          {[['all','All'],['active','Active'],['unread','Unread'],['resolved','Resolved']].map(([v,l]) => (
            <button key={v} className={`tab-btn ${filter === v ? 'active' : ''}`}
              onClick={() => setFilter(v)} style={{ padding: '7px 14px', fontSize: 12 }}>{l}</button>
          ))}
        </div>
        <div className="tabs" style={{ margin: 0 }}>
          {[['all','All'],['info','Info'],['warning','Warning'],['critical','Critical']].map(([v,l]) => (
            <button key={v} className={`tab-btn ${severityFilter === v ? 'active' : ''}`}
              onClick={() => setSeverityFilter(v)} style={{ padding: '7px 14px', fontSize: 12 }}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="loading-screen" style={{ height: 300 }}><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="card"><div className="empty-state">
          <div className="empty-state-icon">🔔</div>
          <div className="empty-state-title">No alerts</div>
          <div className="empty-state-text">You're all caught up! No notifications match your filters.</div>
        </div></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map(alert => (
            <div key={alert._id}
              className={`card alert-card ${!alert.isRead ? 'unread' : ''} ${alert.isResolved ? 'resolved' : ''}`}
              style={{ padding: '16px 20px', cursor: 'pointer', opacity: alert.isResolved ? 0.6 : 1 }}
              onClick={() => !alert.isRead && markRead(alert._id)}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                <div style={{ fontSize: 24, flexShrink: 0 }}>{TYPE_ICON[alert.type] || '🔔'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: !alert.isRead ? 700 : 500, fontSize: 15, marginBottom: 4 }}>
                        {!alert.isRead && <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', marginRight: 8, verticalAlign: 'middle' }} />}
                        {alert.title}
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 13, lineHeight: 1.5 }}>{alert.message}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                      <span className={`badge ${SEVERITY_BADGE[alert.severity]}`}>{SEVERITY_ICON[alert.severity]} {alert.severity}</span>
                      {!alert.isResolved && (
                        <button className="btn btn-ghost btn-sm" onClick={e => { e.stopPropagation(); resolve(alert._id); }}>
                          Resolve
                        </button>
                      )}
                      {alert.isResolved && <span className="badge badge-green">✓ Resolved</span>}
                    </div>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 16 }}>
                    <span>{fmtDate(alert.createdAt)}</span>
                    {alert.relatedInventory && <span>📦 {alert.relatedInventory.name}</span>}
                    {alert.relatedBooking && <span>📅 {alert.relatedBooking.bookingNumber}</span>}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Alert Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <span className="modal-title">Create Alert</span>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input className="form-input" placeholder="Alert title" value={form.title} onChange={set('title')} required />
                </div>
                <div className="form-group">
                  <label className="form-label">Message *</label>
                  <textarea className="form-textarea" placeholder="Describe the alert…" value={form.message} onChange={set('message')} required />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Severity</label>
                    <select className="form-select" value={form.severity} onChange={set('severity')}>
                      <option value="info">Info</option>
                      <option value="warning">Warning</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Target Role</label>
                    <select className="form-select" value={form.targetRole} onChange={set('targetRole')}>
                      <option value="all">All Users</option>
                      <option value="admin">Admin Only</option>
                      <option value="mechanic">Mechanics</option>
                      <option value="customer">Customers</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Sending…' : 'Send Alert'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}